/**
 * System status → Housekeeping: retention of logs, job history and export downloads, and leftover temporary files.
 * The Recycle bin section between them is components/RecycleBinSettings.tsx.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../../api/client';
import { Segmented } from '../../../components/Segmented';
import { toast } from '../../../components/Toasts';
import { TableScroll } from '../../../components/TableScroll';
import { bytes, dateTime, relative } from '../../../lib/format';
import type { HandoffCleanupDTO } from '@prism/contracts';

interface HousekeepingDTO { logRetentionDays: number; jobRetentionDays: number | null; exportRetentionDays: number | null; lastCleanup: { at: string; removed: Record<string, number> } | null }
const REMOVED_LABEL: Record<string, string> = { workflowRuns: 'workflow runs', transfers: 'transfers', transcriptions: 'subtitle jobs', agentJobs: 'agent jobs', watchItems: 'watch-folder records', uploads: 'upload records', processingRuns: 'processing runs', qcRuns: 'QC runs', restores: 'restores' };

/** How long logs and finished job history are kept. */
export function Housekeeping() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['housekeeping'], queryFn: () => api<HousekeepingDTO>('/api/v1/system/housekeeping') });
  const [form, setForm] = useState<{ logs: number; keepJobs: boolean; jobs: number; keepExports: boolean; exports: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const f = form ?? (d ? { logs: d.logRetentionDays, keepJobs: d.jobRetentionDays === null, jobs: d.jobRetentionDays ?? 30, keepExports: d.exportRetentionDays === null, exports: d.exportRetentionDays ?? 30 } : null);
  async function save(runNow: boolean) {
    if (!f) return;
    setBusy(true);
    try {
      await api('/api/v1/system/housekeeping', { method: 'PUT', json: { logRetentionDays: f.logs, jobRetentionDays: f.keepJobs ? null : f.jobs, exportRetentionDays: f.keepExports ? null : f.exports, runNow } });
      await qc.invalidateQueries({ queryKey: ['housekeeping'] });
      setForm(null);
      toast(runNow ? 'Saved and cleaned up' : 'Saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  if (!f) return <div className="skeleton" style={{ height: 160 }} />;
  const removed = d?.lastCleanup ? Object.entries(d.lastCleanup.removed).filter(([, n]) => n > 0) : [];
  return (
    <section className="panel-card" style={{ maxWidth: 760 }} data-testid="housekeeping">
      <p className="section-label">Retention</p>
      <div className="field"><label htmlFor="hk-logs">Keep service logs for</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input id="hk-logs" className="input" type="number" min={1} max={365} style={{ width: 100 }} value={f.logs} onChange={(e) => setForm({ ...f, logs: Number(e.target.value) })} /> days
        </div></div>
      <div className="field"><label>Finished job history</label>
        <Segmented role="radiogroup" value={f.keepJobs} onChange={(keepJobs) => setForm({ ...f, keepJobs })} options={[
          { value: true, label: 'Keep forever' }, { value: false, label: 'Remove after', testId: 'hk-remove-jobs' },
        ]} />
        {!f.keepJobs && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input className="input" type="number" min={1} max={3650} style={{ width: 100 }} value={f.jobs} onChange={(e) => setForm({ ...f, jobs: Number(e.target.value) })} aria-label="Days to keep job history" /> days
        </div>}
        <span className="help">Removes finished workflow runs, transfers, subtitle jobs, agent jobs, finished restores, older processing and QC runs, and upload and watch-folder records whose asset is gone or that failed. Jobs still running or waiting, each asset’s latest processing and QC, date-trigger records and exports are always kept; Statistics keep their totals. 30 days unless you choose; cleanup runs every hour, in batches.</span>
      </div>
      <div className="field"><label>Export downloads</label>
        <Segmented role="radiogroup" value={f.keepExports} onChange={(keepExports) => setForm({ ...f, keepExports })} options={[
          { value: true, label: 'Keep until deleted' }, { value: false, label: 'Remove after', testId: 'hk-expire-exports' },
        ]} />
        {!f.keepExports && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input className="input" type="number" min={1} max={3650} style={{ width: 100 }} value={f.exports} onChange={(e) => setForm({ ...f, exports: Number(e.target.value) })} aria-label="Days to keep export downloads" /> days
        </div>}
        <span className="help">Exports not sent to an export location are kept in the exports tier so people can download them from PRISM, then removed; the export’s record and lineage stay. Exports written to export locations are never removed.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary" disabled={busy} onClick={() => void save(false)}>Save</button>
        <button className="btn" disabled={busy} onClick={() => void save(true)} data-testid="hk-run-now">Save and clean up now</button>
        <span className="faint" style={{ fontSize: 12 }}>
          {d?.lastCleanup ? `Last cleanup ${relative(d.lastCleanup.at)}${removed.length ? `: removed ${removed.map(([k, n]) => `${n} ${REMOVED_LABEL[k] ?? k}`).join(', ')}` : ', nothing to remove'}` : 'No cleanup yet'}
        </span>
      </div>
    </section>
  );
}

interface OrphanItem { locationId: string; location: string; key: string; kind: string; sizeBytes: number; modifiedAt: string }
interface TempFilesDTO {
  uploads: { at: string; uploads: number; bytes: number } | null;
  cleaned: { at: string; files: number; bytes: number; kept: number; failed: string[] } | null;
  scan: { id: string; at: string; truncated: boolean; errors: string[]; files: number; bytes: number; groups: Array<{ location: string; kind: string; files: number; bytes: number }>; items: OrphanItem[] } | null;
}

/** Leftovers of jobs that stopped half way: found by a scan, removed only on confirmation. */
export function TempFiles() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['temp-files'], queryFn: () => api<TempFilesDTO>('/api/v1/system/temp-files') });
  const [busy, setBusy] = useState<'scan' | 'clean' | null>(null);
  const [showFiles, setShowFiles] = useState(false);
  const d = q.data;
  async function run(kind: 'scan' | 'clean') {
    if (kind === 'clean' && d?.scan && !window.confirm(`Delete ${d.scan.files} file${d.scan.files === 1 ? '' : 's'} (${bytes(d.scan.bytes)}) that nothing in PRISM refers to? This cannot be undone.`)) return;
    setBusy(kind);
    try {
      const r = await api<TempFilesDTO>(`/api/v1/system/temp-files/${kind}`, { method: 'POST', json: kind === 'clean' ? { scanId: d!.scan!.id } : {} });
      qc.setQueryData(['temp-files'], r);
      if (kind === 'scan') toast(r.scan?.files ? `Found ${r.scan.files} leftover files (${bytes(r.scan.bytes)})` : 'No leftover files found');
      else toast(`Removed ${r.cleaned?.files ?? 0} files (${bytes(r.cleaned?.bytes ?? 0)})`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  return (
    <section id="temp-files" className="panel-card" style={{ maxWidth: 760, marginTop: 16, scrollMarginTop: 16 }} data-testid="temp-files">
      <p className="section-label">Temporary files</p>
      <p className="help" style={{ margin: 0 }}>
        Jobs remove their temporary files when they finish or fail. What a stopped worker or a half-finished job leaves behind is cleaned up here.
        Workers remove their own leftover work folders, and uploads left unfinished for a day are removed, automatically every hour.
        {d?.uploads && ` Last: ${d.uploads.uploads} abandoned upload${d.uploads.uploads === 1 ? '' : 's'} (${bytes(d.uploads.bytes)}) removed ${relative(d.uploads.at)}.`}
      </p>
      <p className="help" style={{ margin: 0 }}>
        Files in storage that nothing in PRISM refers to, such as the output of a transcode or export that failed after writing it, are found by a scan of PRISM’s folders on the storage tiers.
        Only files untouched for a day are listed, and nothing is removed until you confirm.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn" disabled={busy !== null} onClick={() => void run('scan')} data-testid="temp-scan">{busy === 'scan' ? 'Scanning…' : 'Scan storage'}</button>
        {d?.scan && d.scan.files > 0 && <button className="btn danger" disabled={busy !== null} onClick={() => void run('clean')} data-testid="temp-clean">{busy === 'clean' ? 'Removing…' : `Remove ${d.scan.files} files (${bytes(d.scan.bytes)})`}</button>}
        <span className="faint" style={{ fontSize: 12 }}>
          {d?.scan ? `Scanned ${relative(d.scan.at)}${d.scan.files ? '' : ': nothing left over'}` : d?.cleaned ? `Last removal ${relative(d.cleaned.at)}: ${d.cleaned.files} files, ${bytes(d.cleaned.bytes)}${d.cleaned.kept ? ` (${d.cleaned.kept} kept: in use again)` : ''}` : 'Not scanned yet'}
        </span>
      </div>
      {d?.scan && d.scan.files > 0 && (
        <>
          <TableScroll>
          <table className="table" data-testid="temp-groups">
            <thead><tr><th>Location</th><th>What</th><th style={{ textAlign: 'right' }}>Files</th><th style={{ textAlign: 'right' }}>Size</th></tr></thead>
            <tbody>{d.scan.groups.map((g) => <tr key={`${g.location}|${g.kind}`}><td className="wrap">{g.location}</td><td className="wrap">{g.kind}</td><td className="nowrap" style={{ textAlign: 'right' }}>{g.files}</td><td className="nowrap" style={{ textAlign: 'right' }}>{bytes(g.bytes)}</td></tr>)}</tbody>
          </table>
          </TableScroll>
          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setShowFiles(!showFiles)}>{showFiles ? 'Hide files' : 'Show files'}</button>
          {showFiles && (
            <div className="mono" style={{ fontSize: 12, maxHeight: 280, overflow: 'auto', overflowWrap: 'anywhere' }}>
              {d.scan.items.map((i) => <div key={`${i.locationId}/${i.key}`}>{i.location}: {i.key} · {bytes(i.sizeBytes)} · {dateTime(i.modifiedAt)}</div>)}
              {d.scan.files > d.scan.items.length && <div className="faint">and {d.scan.files - d.scan.items.length} more</div>}
            </div>
          )}
        </>
      )}
      {d?.scan?.truncated && <p className="help" style={{ margin: 0 }}>The scan stopped at 50,000 files; remove these and scan again.</p>}
      {d?.scan?.errors.map((e) => <p key={e} className="help" style={{ margin: 0, color: 'var(--danger)' }}>Could not scan {e}</p>)}
    </section>
  );
}

/**
 * Hand-off folders past their edit location's retention (Check in, 1.0.0-rc.7). Cleanup removes only what PRISM wrote
 * there (the manifest's files and the timelines), after a confirmation; what editors added is listed and kept.
 */
export function HandoffCleanup() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['handoff-cleanup'], queryFn: () => api<{ items: HandoffCleanupDTO[] }>('/api/v1/handoffs/cleanup') });
  const [busy, setBusy] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  async function clean(h: HandoffCleanupDTO) {
    const ask = h.folderMissing
      ? `The folder “${h.folder}” is gone from ${h.location.name}, so nothing will be deleted: the hand-off is only marked cleaned up. Continue?`
      : `Remove the ${h.files} file${h.files === 1 ? '' : 's'} (${bytes(h.bytes)}) PRISM wrote into “${h.folder}” on ${h.location.name}? Files editors added stay. This cannot be undone.`;
    if (!window.confirm(ask)) return;
    setBusy(h.handoffId);
    try {
      const r = await api<{ deleted: number; kept: string[] }>(`/api/v1/handoffs/${h.handoffId}/cleanup`, { method: 'POST', json: { confirm: true } });
      if (h.folderMissing) toast(`“${h.folder}” is marked cleaned up`);
      else toast(`Removed ${r.deleted} file${r.deleted === 1 ? '' : 's'}${r.kept.length ? `; ${r.kept.length} added by editors ${r.kept.length === 1 ? 'is' : 'are'} still there` : ''}`);
      await qc.invalidateQueries({ queryKey: ['handoff-cleanup'] });
      await qc.invalidateQueries({ queryKey: ['handoffs'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  return (
    <section className="panel-card" style={{ maxWidth: 760, marginTop: 16 }} data-testid="handoff-cleanup">
      <p className="section-label">Hand-off folders</p>
      <p className="help" style={{ margin: 0 }}>
        Send to edit folders kept longer than their edit location’s retention, counted from their check-in or last activity.
        Cleanup removes only what PRISM wrote there: the media, graphics, subtitles, timelines, manifest and READMEs. Anything editors added (in _to_prism/ or elsewhere) is listed and kept.
        Hand-offs removed from the Import list are here too; a folder that is already gone is only marked cleaned up.
      </p>
      {items.length === 0 ? <p className="faint" style={{ margin: 0, fontSize: 12 }} data-testid="handoff-cleanup-empty">{q.isLoading ? 'Looking…' : 'Nothing to clean up.'}</p> : (
        <TableScroll>
        <table className="table">
          <thead><tr><th>Hand-off</th><th>Location</th><th>Last activity</th><th style={{ textAlign: 'right' }}>PRISM wrote</th><th aria-label="Actions" /></tr></thead>
          <tbody>{items.map((h) => (
            <tr key={h.handoffId} data-testid="handoff-cleanup-row">
              <td className="wrap" style={{ minWidth: 200 }}><div style={{ fontWeight: 560 }}>{h.name}</div><div className="mono faint" style={{ fontSize: 11.5 }}>{h.folder}</div></td>
              <td className="muted wrap">{h.location.name} · kept {h.retentionDays} days</td>
              <td className="muted wrap" title={dateTime(h.lastActivity)}>{relative(h.lastActivity)}{h.checkedInAt ? ' · checked in' : ''}</td>
              <td className="nowrap" style={{ textAlign: 'right' }}>{h.folderMissing
                ? <span className="pill warning" title="Two checks minutes apart did not find this folder: cleanup deletes nothing" data-testid="handoff-folder-gone">folder gone</span>
                : <>{h.files} files · {bytes(h.bytes)}</>}</td>
              <td className="actions"><button className="btn danger sm" disabled={busy !== null} onClick={() => void clean(h)} data-testid="handoff-clean">{busy === h.handoffId ? 'Removing…' : 'Clean up…'}</button></td>
            </tr>
          ))}</tbody>
        </table>
        </TableScroll>
      )}
    </section>
  );
}
